import React, { Component } from "react";
import { getMessage } from "@buzunarul/shared-constants";

// CVA styling imports - UNIFIED MIGRATION
import { cn, button } from "../styles/cva-v2";

import Alert from "./primitives/Alert/Alert";
import { handleComponentError, EnhancedError, ErrorType, ErrorSeverity } from "../utils/errorHandling";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /**
   * Nume personalizat pentru componentă (pentru logging)
   */
  componentName?: string;
  /**
   * 🚀 React 19 Enhanced: Fallback component for better error recovery
   */
  fallback?: React.ComponentType<{ error: EnhancedError; retry: () => void; canRetry: boolean }>;
  /**
   * 🚀 React 19 Enhanced: Custom error handler for telemetry
   */
  onError?: (error: EnhancedError, errorInfo: React.ErrorInfo) => void;
  /**
   * 🚀 React 19 Enhanced: Enable automatic recovery for certain error types
   */
  enableAutoRecovery?: boolean;
  /**
   * 🚀 React 19 Enhanced: Isolation level for error boundary
   */
  isolationLevel?: 'component' | 'page' | 'app';
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: EnhancedError | null;
  /**
   * 🚀 React 19 Enhanced: Auto-recovery state
   */
  isRecovering: boolean;
  /**
   * 🚀 React 19 Enhanced: Error classification for better handling
   */
  errorType: 'hydration' | 'async' | 'use-hook' | 'chunk-load' | 'runtime' | 'unknown';
}

/**
 * Enhanced Error Boundary cu sistem centralizat de error handling
 * 
 * FEATURES:
 * - Logging structurat cu context complet
 * - Clasificare automată a erorilor
 * - Toast notifications pentru developeri (poate fi disabled în producție)
 * - Recovery capabilities și retry mechanisms
 * - React 19 Enhanced Error Reporting cu reportError API
 * - Improved hydration error handling
 * - Better error boundary recovery patterns
 */
class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  private retryCount: number = 0;
  private readonly maxRetries: number = 2;
  private recoveryTimer: NodeJS.Timeout | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      isRecovering: false,
      errorType: 'unknown'
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // 🚀 React 19: Enhanced error classification for use() hook and other new patterns
    const isPromiseRejection = error.name === 'ChunkLoadError' || 
                              error.message.includes('Loading chunk') ||
                              error.message.includes('Loading CSS chunk') ||
                              error.message.includes('Promise rejected');
    
    const isUseHookError = error.message.includes('use()') ||
                          error.stack?.includes('use(') ||
                          error.name === 'SuspenseException';

    const isHydrationError = error.message?.includes('hydration') || 
                           error.message?.includes('server') || 
                           error.message?.includes('client');

    const isAsyncError = error.message?.includes('async') || 
                        error.message?.includes('promise') ||
                        error.message?.includes('await');

    // 🚀 React 19: Determine error type for better recovery strategies
    let errorType: 'hydration' | 'async' | 'use-hook' | 'chunk-load' | 'runtime' | 'unknown' = 'unknown';
    if (isHydrationError) {
      errorType = 'hydration';
    } else if (isUseHookError) {
      errorType = 'use-hook';
    } else if (isPromiseRejection) {
      errorType = 'chunk-load';
    } else if (isAsyncError) {
      errorType = 'async';
    } else {
      errorType = 'runtime';
    }

    // React 19: Use reportError for better error tracking
    if (typeof window !== 'undefined' && 'reportError' in window) {
      window.reportError(error);
    }

    // Create a proper EnhancedError instance
    const enhancedError = new EnhancedError(
      error.message,
      ErrorType.COMPONENT,
      ErrorSeverity.ERROR,
      {
        operation: 'render',
        component: 'ErrorBoundary',
        additionalData: {
          isPromiseRejection,
          isUseHookError,
          errorType
        }
      },
      error
    );

    return { 
      hasError: true,
      isRecovering: false,
      errorType,
      error: enhancedError
    };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const componentName = this.props.componentName || 'UnknownComponent';
    
    // React 19 Enhanced Error Classification
    const isHydrationError = error.message?.includes('hydration') || 
                           error.message?.includes('server') || 
                           error.message?.includes('client');
    const isAsyncError = error.message?.includes('async') || 
                        error.message?.includes('promise') ||
                        error.message?.includes('await');
    const isUseHookError = error.message?.includes('use()') ||
                          error.stack?.includes('use(') ||
                          error.name === 'SuspenseException';
    
    // Folosește noul sistem enhanced de error handling
    const enhancedError = handleComponentError(error, errorInfo, componentName);
    
    // Add React 19 specific error context
    if (isHydrationError) {
      if (enhancedError.context && typeof enhancedError.context === 'object') {
        const contextUpdate = {
          errorType: 'hydration',
          isRecoverable: true
        } as const;
        Object.assign(enhancedError.context, contextUpdate);
      }
    } else if (isAsyncError || isUseHookError) {
      if (enhancedError.context && typeof enhancedError.context === 'object') {
        const contextUpdate = {
          errorType: (isUseHookError ? 'use-hook' : 'async'),
          isRecoverable: true,
          useHookError: isUseHookError
        } as const;
        Object.assign(enhancedError.context, contextUpdate);
      }
    }
    
    // Actualizează state-ul cu enhanced error
    this.setState({ error: enhancedError });

    // 🚀 React 19 Enhanced: Auto-recovery for certain error types
    if (this.props.enableAutoRecovery) {
      this.attemptAutoRecovery();
    }

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(enhancedError, errorInfo);
    }

    // React 19 Enhanced Error Reporting
    // Report to error tracking service with enhanced context
    if (typeof window !== 'undefined' && 'reportError' in window) {
      // Use React 19's improved error reporting with additional context
      const errorWithContext = new Error(error.message);
      errorWithContext.cause = {
        originalError: error,
        componentName,
        isHydrationError,
        isAsyncError,
        errorInfo,
        isolationLevel: this.props.isolationLevel || 'component'
      };
      window.reportError(errorWithContext);
    }

    // În development, afișează și în console detaliile complete
    if (process.env.NODE_ENV === 'development') {
      console.group('🔍 [ERROR BOUNDARY] Complete Error Details');
      console.error('Original Error:', error);
      console.error('Error Info:', errorInfo);
      console.error('Enhanced Error:', enhancedError.toLogObject());
      console.log('React 19 Context:', { isHydrationError, isAsyncError });
      console.groupEnd();
    }
  }

  /**
   * 🚀 React 19 Enhanced: Auto-recovery for certain error types
   */
  private attemptAutoRecovery = () => {
    const { errorType } = this.state;
    const isRecoverable = errorType === 'hydration' || 
                         errorType === 'use-hook' || 
                         errorType === 'chunk-load';

    if (isRecoverable && this.retryCount < this.maxRetries) {
      this.setState({ isRecovering: true });
      
      // Different recovery strategies based on error type
      let recoveryDelay = 1000; // Default 1 second
      
      if (errorType === 'hydration') {
        recoveryDelay = 100; // Quick recovery for hydration errors
      } else if (errorType === 'chunk-load') {
        recoveryDelay = 2000; // Longer delay for chunk loading errors
      } else if (errorType === 'use-hook') {
        recoveryDelay = 500; // Medium delay for use() hook errors
      }

      this.recoveryTimer = setTimeout(() => {
        this.handleRetry();
      }, recoveryDelay);

      console.info(`🔄 [ERROR BOUNDARY] Auto-recovery initiated for ${errorType} error (delay: ${recoveryDelay}ms)`);
    }
  };

  /**
   * Încercă să recupereze din eroare prin remounting
   */
  handleRetry = () => {
    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      this.setState({ 
        hasError: false, 
        error: null,
        isRecovering: false,
        errorType: 'unknown'
      });
      
      // Clear recovery timer
      if (this.recoveryTimer) {
        clearTimeout(this.recoveryTimer);
        this.recoveryTimer = null;
      }
      
      // Log retry attempt
      console.info(`🔄 [ERROR BOUNDARY] Retry attempt ${this.retryCount}/${this.maxRetries} for component ${this.props.componentName || 'Unknown'}`);
    }
  };

  /**
   * Reset error boundary (pentru recovery externă)
   */
  override componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.hasError && prevProps.children !== this.props.children) {
      // Props s-au schimbat, poate fi safe să resetez
      this.setState({ hasError: false, error: null });
      this.retryCount = 0;
    }
  }

  override render() {
    if (this.state.hasError && this.state.error) {
      const canRetry = this.retryCount < this.maxRetries;
      
      // 🚀 React 19 Enhanced: Use custom fallback if provided
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return (
          <FallbackComponent 
            error={this.state.error} 
            retry={this.handleRetry} 
            canRetry={canRetry}
          />
        );
      }

      // 🚀 React 19 Enhanced: Show recovery loading state
      if (this.state.isRecovering) {
        return (
          <div className="p-4 error-boundary-container">
            <Alert
              variant="warning"
              title="🔄 Recuperare în curs..."
              className="my-8"
              data-testid="error-boundary-recovering"
            >
              Se încercă recuperarea automată din eroarea {this.state.errorType}...
            </Alert>
          </div>
        );
      }
      
      return (
        <div className="p-4 error-boundary-container">
          <Alert
            variant="error"
            title={getMessage('EROARE_TITLU')}
            className="my-8"
            data-testid="error-boundary-alert"
          >
            {this.state.error.userMessage}
          </Alert>

          {/* 🚀 React 19 Enhanced: Error type indicator */}
          {this.state.errorType !== 'unknown' && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="text-amber-600 font-medium">
                  Tip eroare: {this.state.errorType}
                </span>
                {this.state.errorType === 'hydration' && (
                  <span className="text-xs text-amber-500">
                    (Diferență între server și client)
                  </span>
                )}
                {this.state.errorType === 'use-hook' && (
                  <span className="text-xs text-amber-500">
                    (Eroare în hook-ul use())
                  </span>
                )}
                {this.state.errorType === 'chunk-load' && (
                  <span className="text-xs text-amber-500">
                    (Eroare la încărcarea modulelor)
                  </span>
                )}
              </div>
            </div>
          )}
          
          {/* Recovery Controls */}
          <div className="flex gap-4 justify-center mt-4">
            {canRetry && (
              <button
                onClick={this.handleRetry}
                className={cn(
                  button({ variant: "primary", size: "md" }),
                  "transition-colors"
                )}
                data-testid="error-boundary-retry"
              >
                🔄 Încearcă din nou ({this.maxRetries - this.retryCount} încercări rămase)
              </button>
            )}
            
            <button
              onClick={() => window.location.reload()}
              className={cn(
                button({ variant: "secondary", size: "md" }),
                "transition-colors"
              )}
              data-testid="error-boundary-reload"
            >
              🔄 Reîncarcă pagina
            </button>
          </div>

          {/* Development Error Details */}
          {process.env.NODE_ENV === 'development' && (
            <details className="p-4 mt-6 bg-gray-100 rounded-lg">
              <summary className="font-semibold text-gray-700 cursor-pointer">
                🔍 Detalii tehnice pentru dezvoltatori
              </summary>
              <div className="mt-4 space-y-2">
                <p><strong>Tip eroare:</strong> {this.state.error.type}</p>
                <p><strong>Severitate:</strong> {this.state.error.severity}</p>
                <p><strong>Operație:</strong> {this.state.error.context.operation}</p>
                <p><strong>Componentă:</strong> {this.state.error.context.component}</p>
                <p><strong>Timestamp:</strong> {this.state.error.timestamp}</p>
                <p><strong>React 19 Error Type:</strong> {this.state.errorType}</p>
                <p><strong>Isolation Level:</strong> {this.props.isolationLevel || 'component'}</p>
                <p><strong>Auto-recovery:</strong> {this.props.enableAutoRecovery ? 'Enabled' : 'Disabled'}</p>
                {this.state.error.originalError && (
                  <div className="mt-4">
                    <p><strong>Mesaj original:</strong></p>
                    <pre className="overflow-auto p-2 text-xs bg-red-50 rounded">
                      {this.state.error.originalError.message}
                    </pre>
                  </div>
                )}
                {this.state.error.originalError?.stack && (
                  <div className="mt-4">
                    <p><strong>Stack trace:</strong></p>
                    <pre className="overflow-auto p-2 max-h-40 text-xs bg-gray-50 rounded">
                      {this.state.error.originalError.stack}
                    </pre>
                  </div>
                )}
              </div>
            </details>
          )}
        </div>
      );
    }
    
    return this.props.children;
  }

  // 🚀 React 19 Enhanced: Cleanup on unmount
  override componentWillUnmount() {
    if (this.recoveryTimer) {
      clearTimeout(this.recoveryTimer);
    }
  }
}

export default ErrorBoundary;
